/**
 * EditorTypes — Shared constants, types, enums, and layout values for MapEditorScene.
 */
import { TileType, CANVAS_WIDTH, CANVAS_HEIGHT, EnemyType, DecorType } from '@/config/GameConfig';

// ── Tile painting tools — split into structural and active (hazard) groups ──
export type TileTool = { type: TileType; label: string; key: string; color: number };

export const STRUCTURE_TILE_TOOLS: TileTool[] = [
  { type: TileType.GROUND,    label: 'Grass',     key: 'G', color: 0x2a2a3e },
  { type: TileType.WALL,      label: 'Wall',      key: 'W', color: 0x555577 },
  { type: TileType.PATH,      label: 'Path',      key: 'P', color: 0x886633 },
  { type: TileType.GRILLE,    label: 'Grille',    key: 'R', color: 0x444455 },
];

export const SPECIAL_TILE_TOOLS: TileTool[] = [
  { type: TileType.PLACEMENT,        label: 'Placement',        key: 'Z', color: 0x335533 },
  { type: TileType.BROKEN_PLACEMENT, label: 'Broken Place',     key: 'K', color: 0x3a2a2a },
  { type: TileType.BASE,             label: 'Base',             key: 'B', color: 0x2244cc },
  { type: TileType.SPAWN,            label: 'Spawn',            key: 'S', color: 0xcc2222 },
];

export const ACTIVE_TILE_TOOLS: TileTool[] = [
  { type: TileType.MUD,       label: 'Mud',       key: 'M', color: 0x665533 },
  { type: TileType.WATER,     label: 'Water',     key: 'E', color: 0x224488 },
  { type: TileType.MAGMA,     label: 'Magma',     key: 'L', color: 0xcc4411 },
];

export const TILE_TOOLS: TileTool[] = [...STRUCTURE_TILE_TOOLS, ...SPECIAL_TILE_TOOLS, ...ACTIVE_TILE_TOOLS];

// ── Decoration tools (purely visual overlays) ──
export const DECOR_TOOLS: { type: DecorType; label: string; texture: string; color: number }[] = [
  { type: DecorType.TREE,        label: 'Tree',       texture: 'decor_arbre',       color: 0x227722 },
  { type: DecorType.COLUMN,      label: 'Column',     texture: 'decor_colonne',     color: 0x998877 },
  { type: DecorType.CHASM,       label: 'Chasm',      texture: 'decor_gouffre',     color: 0x111122 },
  { type: DecorType.CONDUIT,     label: 'Conduit',    texture: 'decor_conduit',     color: 0x556677 },
  { type: DecorType.BROKEN_WALL, label: 'Broken Wall',texture: 'decor_broken_wall', color: 0x777766 },
  { type: DecorType.DEAD_TREE,   label: 'Dead Tree',  texture: 'decor_dead_tree',   color: 0x554433 },
  { type: DecorType.FLAQUE,      label: 'Puddle',     texture: 'decor_flaque',      color: 0x335577 },
  { type: DecorType.ROCHER,      label: 'Rock',       texture: 'decor_rocher',      color: 0x666655 },
  { type: DecorType.SOUCHE,      label: 'Stump',      texture: 'decor_souche',      color: 0x665544 },
  { type: DecorType.BARRIERE,    label: 'Fence',      texture: 'decor_barriere',    color: 0x886644 },
  { type: DecorType.BROKEN_STATUE, label: 'Statue',   texture: 'decor_broken_statue', color: 0x888877 },
];

// ── Resource tools (harvestable decor — yields Co or Electro) ──
export const RESOURCE_TOOLS: { type: DecorType; label: string; texture: string; color: number; yield: string }[] = [
  { type: DecorType.CONNARDIUM,  label: 'Connardium Mineral', texture: 'decor_connardium',  color: 0x44aa33, yield: '+5 Co' },
  { type: DecorType.GARBAGE,     label: 'Garbage',            texture: 'decor_garbage',      color: 0x776655, yield: '+5 ◻' },
  { type: DecorType.RUINS,       label: 'Ruins',              texture: 'decor_ruines',       color: 0x887766, yield: '+3 Co +3 ◻' },
  { type: DecorType.CAISSE,      label: 'Crate',              texture: 'decor_caisse_0',     color: 0x997744, yield: '+1 ◻' },
  { type: DecorType.BARREL,      label: 'Barrel',             texture: 'decor_barrel',       color: 0x886644, yield: '+1 Co' },
];

export const RICHNESS_LABELS = ['Barren', 'Poor', 'Normal', 'Rich', 'Abundant'] as const;
export const HAZARD_LABELS = ['None', 'Low', 'Normal', 'High', 'Extreme'] as const;
export const INTENSITY_LABELS = ['Trivial', 'Easy', 'Normal', 'Hard', 'Brutal'] as const;

export const DIFFICULTIES = [
  'Beginner', 'Easy', 'Normal', 'Moderate',
  'Challenging', 'Hard', 'Very Hard', 'Expert', 'Nightmare', 'Infernal',
] as const;

export const ALL_ENEMY_TYPES: EnemyType[] = [
  EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK,
  EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP, EnemyType.HIVE,
  EnemyType.BRUTE, EnemyType.BOMBER, EnemyType.SPECTER, EnemyType.HEALER,
];

// Layout constants
export const TOOLBAR_W = 180;
export const TOOLBAR_X = 0;
export const PROPS_W = 220;
export const PROPS_X = CANVAS_WIDTH - PROPS_W;
export const GRID_AREA_X = TOOLBAR_W + 4;
export const GRID_AREA_W = CANVAS_WIDTH - TOOLBAR_W - PROPS_W - 8;
export const GRID_AREA_Y = 40;
export const GRID_AREA_H = CANVAS_HEIGHT - 50;

export const MAX_UNDO = 60;

export const COMPLEXITY_LABELS = ['Simple', 'Medium', 'Complex', 'Extreme', 'Infernal'];

// ── Editor modes ──
export enum EditorMode {
  PAINT = 'paint',
  PATH = 'path',
  DECOR = 'decor',
  RESOURCE = 'resource',
  ERASE = 'erase',
}

// ── Undo/Redo snapshot ──
export interface EditorSnapshot {
  grid: TileType[][];
  decorations: import('@/config/GameConfig').DecorDef[];
  paths: { col: number; row: number }[][];
  cols: number;
  rows: number;
}

// ── Tile texture mapping ──
export const TILE_TEXTURES: Record<TileType, string> = {
  [TileType.GROUND]:    'tile_gazon',
  [TileType.WALL]:      'tile_mur',
  [TileType.PATH]:      'tile_goudron',
  [TileType.PLACEMENT]: 'tile_placement',
  [TileType.BASE]:      'tile_electro',
  [TileType.SPAWN]:     'tile_electro',
  [TileType.MUD]:       'tile_boue',
  [TileType.WATER]:     'tile_eau',
  [TileType.GRILLE]:    'tile_grille',
  [TileType.MAGMA]:     'tile_magma',
  [TileType.BROKEN_PLACEMENT]: 'tile_broken_placement',
};

// ── Decoration texture mapping ──
export const DECOR_TEXTURES: Record<DecorType, string> = {
  [DecorType.TREE]:        'decor_arbre',
  [DecorType.RUINS]:       'decor_ruines',
  [DecorType.COLUMN]:      'decor_colonne',
  [DecorType.CHASM]:       'decor_gouffre',
  [DecorType.CONDUIT]:     'decor_conduit',
  [DecorType.BARREL]:      'decor_barrel',
  [DecorType.BROKEN_WALL]: 'decor_broken_wall',
  [DecorType.CAISSE]:      'decor_caisse_0',
  [DecorType.DEAD_TREE]:   'decor_dead_tree',
  [DecorType.FLAQUE]:      'decor_flaque',
  [DecorType.ROCHER]:      'decor_rocher',
  [DecorType.SOUCHE]:      'decor_souche',
  [DecorType.CONNARDIUM]:  'decor_connardium',
  [DecorType.GARBAGE]:     'decor_garbage',
  [DecorType.BARRIERE]:    'decor_barriere',
  [DecorType.BROKEN_STATUE]: 'decor_broken_statue',
};
